'use client'

import React from 'react'
import { useTranslation } from 'react-i18next'
import type { VoiceUsageResponse } from '@/features/billing/api-client'
import { SettingRow } from '@kiao-voice/ui-kit'
import { billingStatClassName, billingUnitClassName } from '@/components/billing-stat'

/**
 * 語音輸入 row in the 01 用量 section (account/billing — numbered-section design,
 * app-account.html). Renders one `.brow`: a label + a `N / 100 句` stat (or the
 * 無上限 unit for paid plans). While the first probe is in flight (`loading`) it
 * shows a neutral line; once settled, `usage === null` means the probe failed →
 * an error line (so the figure reads as unavailable, not zero). The monthly
 * reset date now lives in the section description, so it's not repeated here.
 */
export function VoiceUsagePanel({
  usage,
  loading = false,
}: {
  usage: VoiceUsageResponse | null
  /** True while the first quota probe is still in flight (avoids flashing the
   *  error line before the probe resolves). */
  loading?: boolean
}) {
  const { t } = useTranslation('web')
  const quota = usage?.monthlyQuota ?? 0

  return (
    <SettingRow
      data-testid="billing-voice-usage"
      label={t('billing.voiceTitle')}
      description={t('billing.voicePlanNote')}
      control={
        <span className="flex flex-wrap items-center justify-end gap-3.5 text-right">
          {usage === null && loading ? (
            <span className={billingUnitClassName} data-testid="billing-voice-usage-loading">
              {t('billing.loading')}
            </span>
          ) : usage === null ? (
            <span className={billingUnitClassName} data-testid="billing-voice-usage-error">
              {t('billing.voiceUsageError')}
            </span>
          ) : (
            <span className={billingStatClassName} data-testid="billing-voice-usage-count">
              {usage.sentenceCount}
              {usage.isUnlimited ? (
                <span className={billingUnitClassName} data-testid="billing-voice-usage-unlimited">
                  {t('billing.voiceUsageUnlimitedUnit')}
                </span>
              ) : (
                <span className={billingUnitClassName}>{t('billing.voiceUsageQuotaUnit', { quota })}</span>
              )}
            </span>
          )}
        </span>
      }
    />
  )
}
